import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQueue } from "../contexts/QueueContext";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import { useDocumentStore } from "../store/documentState";
import { useDealsStore } from "../store/dealsStore";
import { uploadFilesAsync as uploadFilesToDrive, base64ToBytes } from "../services/api/drive";
import { normalizePhotoFile } from "../services/imageService";
import { QueueItemLockTimeoutError } from "../services/queueService";
import type { ProjectStatusSnapshot } from "../types/hubspot";
import { getSharedDriveName } from "../utils/env";
import {
  getProjektdokumentationTemplate,
  resetVotToDraft,
  upsertProjektdokumentation,
  updateProjektdokumentationStatus,
} from "../services/api/projektdokumentation";
import { submitVotReport, listVotDriveFiles } from "../services/api/vot";
import type { VotDriveFile, VotReportEntry } from "../interfaces/vot";
import { useTemplateCacheStore } from "../store/templateCacheStore";
import {
  usePhotoAnnotationGallery,
  withMarkedFileNameSuffix,
  isMarkedFileName,
  type PhotoPair,
} from "../hooks/usePhotoAnnotationGallery";
import PhotoAnnotationEditor from "./PhotoAnnotationEditor";
import CaptionIndicator from "./CaptionIndicator";
import {
  ProjektfreigabeStatus,
  ProjektdokumentationChecklistItem,
  ProjektdokumentationFormData,
  ProjektdokumentationSection,
  ProjektdokumentationStoredRecord,
  ProjektdokumentationTemplate,
  ProjektdokumentationUploadTarget,
  ProjektdokumentationUpsertResult,
} from "../types/projektdokumentation";
import {
  getSectionProgress,
  isChecklistItemCompleted,
  isChecklistItemVisible,
  validateProjektdokumentation,
} from "./projektdokumentationValidation";
import {
  getNextVotPhotoFileNames,
  getVotSectionPhotoPrefix,
  isVotPhotoFileNameForPrefix,
  isVotPhotoSectionSupported,
  VOT_PHOTO_DIRECTORIES,
  VOT_PHOTO_JPEG_QUALITY,
  VOT_PHOTO_MAX_DIMENSION,
} from "../utils/votPhotoNaming";

interface ProjektdokumentationWizardProps {
  projectId: string;
  phaseId: string;
  disabled?: boolean;
  onCompleted?: (completedAt: string) => void;
}

interface LocalDraftState {
  sections: ProjektdokumentationSection[];
  technicalQuestionsOpen: boolean;
  tightestAccessCm: string;
  heatingRoomAreaApprox: string;
  lowCeilingHeightNote: string;
  pvStatus: "no" | "existing" | "planned" | "";
  drainAvailable: "" | "yes" | "no";
  status: ProjektfreigabeStatus;
}

const statusLabels: Record<ProjektfreigabeStatus, string> = {
  draft: "Entwurf",
  in_review: "In Prüfung",
  ready: "Freigabefähig",
  approved: "Freigegeben",
};

const statusTone: Record<ProjektfreigabeStatus, string> = {
  draft: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
  in_review: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200",
  ready: "bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200",
  approved: "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-200",
};

const emptyDraftState = (): LocalDraftState => ({
  sections: [],
  technicalQuestionsOpen: false,
  tightestAccessCm: "",
  heatingRoomAreaApprox: "",
  lowCeilingHeightNote: "",
  pvStatus: "",
  drainAvailable: "",
  status: "draft",
});

const toDraftState = (
  template: ProjektdokumentationTemplate
): LocalDraftState => {
  const record = template.existingRecord;
  return {
    sections: record?.sections || template.sections,
    technicalQuestionsOpen: record?.technicalQuestionsOpen || false,
    tightestAccessCm:
      typeof record?.tightestAccessCm === "number" ? String(record.tightestAccessCm) : "",
    heatingRoomAreaApprox: record?.heatingRoomAreaApprox || "",
    lowCeilingHeightNote: record?.lowCeilingHeightNote || "",
    pvStatus: record?.pvStatus || "",
    drainAvailable:
      typeof record?.drainAvailable === "boolean"
        ? record.drainAvailable
          ? "yes"
          : "no"
        : "",
    status: record?.status || "draft",
  };
};

export const mergeLocalDraftWithServerState = (
  serverDraft: LocalDraftState,
  localDraft: LocalDraftState
): LocalDraftState => {
  const localSections = new Map(localDraft.sections.map((section) => [section.id, section]));

  return {
    ...serverDraft,
    // Der Katalog vom Server ist führend: Ein alter Browser-Entwurf darf keine
    // zwischenzeitlich hinzugekommenen Pflichtpunkte aus der Checkliste entfernen.
    sections: serverDraft.sections.map((serverSection) => {
      const localSection = localSections.get(serverSection.id);
      const localItems = new Map(localSection?.items.map((item) => [item.id, item]) || []);

      return {
        ...serverSection,
        items: serverSection.items.map((serverItem) => {
          const localItem = localItems.get(serverItem.id);
          if (!localItem) {
            return serverItem;
          }

          // Nur eingegebene Werte wiederherstellen; Pflichtstatus, Labels und
          // Eingabe-Regeln stammen immer aus dem aktuellen Server-Katalog.
          return {
            ...serverItem,
            ...(typeof localItem.checked === "boolean" ? { checked: localItem.checked } : {}),
            ...(typeof localItem.comment === "string" ? { comment: localItem.comment } : {}),
            ...(typeof localItem.valueString === "string" ? { valueString: localItem.valueString } : {}),
            ...("valueNumber" in localItem ? { valueNumber: localItem.valueNumber } : {}),
            ...("valueBoolean" in localItem ? { valueBoolean: localItem.valueBoolean } : {}),
            ...(typeof localItem.valueUrl === "string" ? { valueUrl: localItem.valueUrl } : {}),
          };
        }),
      };
    }),
    technicalQuestionsOpen:
      typeof localDraft.technicalQuestionsOpen === "boolean"
        ? localDraft.technicalQuestionsOpen
        : serverDraft.technicalQuestionsOpen,
    tightestAccessCm:
      typeof localDraft.tightestAccessCm === "string"
        ? localDraft.tightestAccessCm
        : serverDraft.tightestAccessCm,
    heatingRoomAreaApprox:
      typeof localDraft.heatingRoomAreaApprox === "string"
        ? localDraft.heatingRoomAreaApprox
        : serverDraft.heatingRoomAreaApprox,
    lowCeilingHeightNote:
      typeof localDraft.lowCeilingHeightNote === "string"
        ? localDraft.lowCeilingHeightNote
        : serverDraft.lowCeilingHeightNote,
    pvStatus: localDraft.pvStatus ?? serverDraft.pvStatus,
    drainAvailable: localDraft.drainAvailable ?? serverDraft.drainAvailable,
    // Der Status selbst darf nicht aus einem möglicherweise alten Browser-Cache kommen.
    status: serverDraft.status,
  };
};

const buildPayload = (
  projectId: string,
  draft: LocalDraftState,
  user: { id: string; email: string } | null
): ProjektdokumentationFormData => ({
  // technicalQuestionsOpen wird primär aus dem Checklist-Item abgeleitet,
  // damit Anzeige- und Persistenzzustand nicht auseinanderlaufen.
  projectId,
  sections: draft.sections,
  technicalQuestionsOpen: (() => {
    const item = draft.sections
      .flatMap((section) => section.items)
      .find((entry) => entry.id === "technical-questions-status");
    if (typeof item?.valueBoolean === "boolean") {
      return !item.valueBoolean;
    }
    return draft.technicalQuestionsOpen;
  })(),
  tightestAccessCm: /^\d+$/.test(draft.tightestAccessCm) ? Number(draft.tightestAccessCm) : null,
  heatingRoomAreaApprox: draft.heatingRoomAreaApprox.trim() || undefined,
  lowCeilingHeightNote: draft.lowCeilingHeightNote.trim() || undefined,
  pvStatus: draft.pvStatus || null,
  drainAvailable:
    draft.drainAvailable === "yes" ? true : draft.drainAvailable === "no" ? false : null,
  updatedByEmail: user?.email ?? "",
  updatedByUserId: user?.id ?? "",
});

const toStoredRecord = (
  result: ProjektdokumentationUpsertResult,
  payload: ProjektdokumentationFormData,
  previous: ProjektdokumentationStoredRecord | null
): ProjektdokumentationStoredRecord => ({
  projectId: result.projectId,
  status: result.status,
  sections: result.sections,
  completenessPercent: result.completenessPercent,
  isReadyForApproval: result.isReadyForApproval,
  missingRequiredItems: result.missingRequiredItems,
  technicalQuestionsOpen: payload.technicalQuestionsOpen,
  tightestAccessCm: payload.tightestAccessCm,
  heatingRoomAreaApprox: payload.heatingRoomAreaApprox,
  lowCeilingHeightNote: payload.lowCeilingHeightNote,
  pvStatus: payload.pvStatus,
  drainAvailable: payload.drainAvailable,
  updatedByEmail: payload.updatedByEmail,
  updatedByUserId: payload.updatedByUserId,
  createdAt: previous?.createdAt ?? result.updatedAt,
  updatedAt: result.updatedAt,
  updatedBy: payload.updatedByEmail
    ? {
        id: payload.updatedByUserId,
        username: payload.updatedByEmail,
        email: payload.updatedByEmail,
      }
    : previous?.updatedBy,
  completedAt: previous?.completedAt,
  completedBy: previous?.completedBy,
  completedByEmail: previous?.completedByEmail,
  approvedAt: previous?.approvedAt,
  approvedBy: previous?.approvedBy,
  approvedByEmail: previous?.approvedByEmail,
});

const formatItemValue = (item: ProjektdokumentationChecklistItem): string | null => {
  switch (item.inputType) {
    case "text":
      return item.valueString?.trim() || null;
    case "select": {
      const value = item.valueString?.trim();
      if (!value) return null;
      return item.options?.find((option) => option.value === value)?.label || value;
    }
    case "number":
      return typeof item.valueNumber === "number" && Number.isFinite(item.valueNumber)
        ? `${item.valueNumber}${item.inputLabel ? ` ${item.inputLabel}` : ""}`
        : null;
    case "boolean":
      return typeof item.valueBoolean === "boolean" ? (item.valueBoolean ? "Ja" : "Nein") : null;
    case "url":
      return item.valueUrl?.trim() || null;
    default:
      return null;
  }
};

const VOT_REPORT_SUPPRESSED_ITEM_IDS = new Set([
  // Der Wert ist identisch zu "tightest-access-recorded" und soll im Bericht nicht doppelt erscheinen.
  "tightest-access-confirmed",
]);

const VOT_REPORT_QUESTION_LABELS: Record<string, string> = {
  "tightest-access-recorded": "Engste Stelle Zuweg / Einbringung",
  "heating-room-area-recorded": "Ungefähre Fläche Heizraum",
  "drain-availability-recorded": "Abfluss im Heizraum / Keller",
  "low-ceiling-height-present": "Niedrige Deckenhöhe vorhanden",
  "low-ceiling-height-documented": "Niedrige Deckenhöhe dokumentiert",
  "pv-status-recorded": "PV-Anlage vorhanden/geplant",
  "technical-questions-status": "Offene technische Fragen",
};

const VOT_REPORT_VALUE_UNITS: Record<string, string> = {
  "tightest-access-recorded": "cm",
  "tightest-access-confirmed": "cm",
  "heating-room-area-recorded": "m²",
};

const getVotReportQuestion = (item: ProjektdokumentationChecklistItem): string =>
  VOT_REPORT_QUESTION_LABELS[item.id] || item.label;

const valueIncludesUnit = (value: string, unit: string): boolean => {
  if (unit === "m²") {
    return /(?:^|[\d\s])(?:m²|m2|qm|quadratmeter)(?=$|[\s.,;)])/i.test(value);
  }

  return /(?:^|[\d\s])cm(?=$|[\s.,;)])/i.test(value);
};

const formatVotReportMeasuredValue = (
  item: ProjektdokumentationChecklistItem,
  value: string
): string => {
  const unit = VOT_REPORT_VALUE_UNITS[item.id];
  if (!unit || valueIncludesUnit(value, unit)) {
    return value;
  }

  return `${value} ${unit}`;
};

const formatVotBooleanValue = (item: ProjektdokumentationChecklistItem): string | null => {
  if (typeof item.valueBoolean !== "boolean") {
    return null;
  }

  if (item.id === "technical-questions-status") {
    return item.valueBoolean
      ? "Nein, keine offenen technischen Fragen"
      : "Ja, offene technische Fragen vorhanden";
  }

  return item.valueBoolean ? "Ja" : "Nein";
};

const formatVotReportValue = (item: ProjektdokumentationChecklistItem): string | null => {
  if (item.inputType === "boolean") {
    const value = formatVotBooleanValue(item);
    return value ? `Antwort: ${value}` : null;
  }

  if (item.inputType === "select") {
    const value = formatItemValue(item);
    return value ? `Antwort: ${value}` : null;
  }

  if (item.inputType === "url") {
    const value = formatItemValue(item);
    return value ? "Link hinterlegt" : null;
  }

  const value = formatItemValue(item);
  return value ? `Wert: ${formatVotReportMeasuredValue(item, value)}` : null;
};

const buildVotReportEntries = (
  data: ProjektdokumentationFormData,
  hasPhotosForSection: (section: ProjektdokumentationSection) => boolean = () => false
): VotReportEntry[] => {
  const entries: VotReportEntry[] = [];

  data.sections.forEach((section) => {
    const sectionHasPhotos =
      isVotPhotoSectionSupported(section.id, section.code) && hasPhotosForSection(section);

    section.items.forEach((item) => {
      if (
        VOT_REPORT_SUPPRESSED_ITEM_IDS.has(item.id) ||
        !isChecklistItemVisible(data, section.id, item.id)
      ) {
        return;
      }

      const parts: string[] = [];
      const value = formatVotReportValue(item);
      const comment = item.comment?.trim();
      if (value) parts.push(value);
      if (comment) parts.push(`Bemerkung: ${comment}`);

      if (parts.length === 0) {
        return;
      }

      entries.push({
        section: `[${section.code}] ${section.title}`,
        sectionId: section.id,
        sectionCode: section.code,
        itemId: item.id,
        linkUrl: item.inputType === "url" ? item.valueUrl?.trim() || undefined : undefined,
        question: getVotReportQuestion(item),
        answer: parts.join("\n"),
      });
    });

    if (sectionHasPhotos) {
      entries.push({
        section: `[${section.code}] ${section.title}`,
        sectionId: section.id,
        sectionCode: section.code,
        question: "Fotodokumentation",
        answer: "Bilddokumentation",
        photoPrefix: getVotSectionPhotoPrefix(section),
      });
    }
  });

  if (data.lowCeilingHeightNote?.trim()) {
    entries.push({
      section: "[K] Technische Angaben",
      question: "Niedrige Deckenhöhe - Notiz",
      answer: `Bemerkung: ${data.lowCeilingHeightNote.trim()}`,
    });
  }

  return entries;
};

const sortVotDriveFiles = (files: VotDriveFile[]): VotDriveFile[] =>
  [...files].sort((a, b) => a.name.localeCompare(b.name, "de"));

// Ticket #1055: Original und markierte Version liegen als zwei getrennte Dateien im selben
// Ordner (Bilder/VOT) — hier per Dateinamens-Konvention (`<original>-markiert.<ext>`) zu je einer
// Kachel zusammengeführt, analog zu pairFotosWithMarkedVersions in ProjektvorbereitungBriefing.tsx.
// Anders als dort gibt es hier keine Ordner-Gruppierung (alle VOT-Fotos liegen in einem Ordner,
// unterschieden per Dateinamens-Präfix pro Checklisten-Sektion) — der Pairing-Key ist daher nur
// der Dateiname.
const pairVotPhotos = (photos: VotDriveFile[]): PhotoPair<VotDriveFile>[] => {
  const byName = new Map(photos.map((photo) => [photo.name, photo] as const));
  const consumedMarkedNames = new Set<string>();
  const pairs: PhotoPair<VotDriveFile>[] = [];

  for (const photo of photos) {
    if (isMarkedFileName(photo.name)) {
      continue;
    }
    const markedName = withMarkedFileNameSuffix(photo.name);
    const marked = byName.get(markedName);
    if (marked) {
      consumedMarkedNames.add(markedName);
    }
    pairs.push({ original: photo, marked });
  }

  // Verwaiste "-markiert"-Dateien (z. B. Original zwischenzeitlich außerhalb der App gelöscht)
  // trotzdem als eigene Kachel zeigen statt sie kommentarlos zu verschlucken.
  for (const photo of photos) {
    if (isMarkedFileName(photo.name) && !consumedMarkedNames.has(photo.name)) {
      pairs.push({ original: photo });
    }
  }

  return pairs;
};

const normalizeImageForVotUpload = async (file: File, fileName: string): Promise<File> => {
  const normalized = await normalizePhotoFile(file, {
    fileName,
    maxDimension: VOT_PHOTO_MAX_DIMENSION,
    qualities: [VOT_PHOTO_JPEG_QUALITY],
  });
  return normalized.file;
};

const base64ToFile = (base64Content: string, fileName: string, mimeType: string): File => {
  return new File([base64ToBytes(base64Content)], fileName, { type: mimeType });
};

const updateBoundFieldsForItem = (
  previous: LocalDraftState,
  itemId: string,
  item: ProjektdokumentationChecklistItem
): Partial<LocalDraftState> => {
  switch (itemId) {
    case "tightest-access-recorded":
    case "tightest-access-confirmed":
      return {
        tightestAccessCm:
          typeof item.valueNumber === "number" ? String(item.valueNumber) : previous.tightestAccessCm,
      };
    case "heating-room-area-recorded":
      return { heatingRoomAreaApprox: item.valueString || previous.heatingRoomAreaApprox };
    case "low-ceiling-height-recorded":
      return { lowCeilingHeightNote: item.valueString || previous.lowCeilingHeightNote };
    case "pv-status-recorded":
      return {
        pvStatus:
          item.valueString === "no" || item.valueString === "existing" || item.valueString === "planned"
            ? item.valueString
            : previous.pvStatus,
      };
    case "drain-availability-recorded":
      return {
        drainAvailable:
          typeof item.valueBoolean === "boolean"
            ? item.valueBoolean
              ? "yes"
              : "no"
            : previous.drainAvailable,
      };
    case "technical-questions-status":
      return {
        technicalQuestionsOpen:
          typeof item.valueBoolean === "boolean" ? !item.valueBoolean : previous.technicalQuestionsOpen,
      };
    default:
      return {};
  }
};

const ProjektdokumentationWizard: React.FC<ProjektdokumentationWizardProps> = ({
  projectId,
  phaseId,
  disabled = false,
  onCompleted,
}) => {
  const { addToQueue } = useQueue();
  const { showToast } = useToast();
  const { user } = useAuth();
  const updatePhaseStatus = useDocumentStore((state) => state.updatePhaseStatus);
  const upsertDeal = useDealsStore((state) => state.upsertDeal);
  const deals = useDealsStore((state) => state.deals);
  const [template, setTemplate] = useState<ProjektdokumentationTemplate | null>(null);
  const [draft, setDraft] = useState<LocalDraftState>(emptyDraftState);
  const [storedRecord, setStoredRecord] = useState<ProjektdokumentationStoredRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [isResettingToDraft, setIsResettingToDraft] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [votDriveFiles, setVotDriveFiles] = useState<VotDriveFile[]>([]);
  const [isLoadingVotDriveFiles, setIsLoadingVotDriveFiles] = useState(false);
  const [votPhotoBusy, setVotPhotoBusy] = useState<Record<string, string>>({});
  const [votPhotoErrors, setVotPhotoErrors] = useState<Record<string, string>>({});
  // Ticket #1055: die gerade gespeicherte, annotierte Version liegt schon lokal vor — direkt als
  // Thumbnail übernehmen statt sie erneut übers Backend zu laden (analog zum thumbnailCache in
  // ProjektvorbereitungBriefing.tsx, hier aber nur als Override, da VOT-Fotos sonst direkt über
  // photo.thumbnailLink angezeigt werden, ohne eigenen Daten-Cache).
  const [votPhotoThumbnailOverrides, setVotPhotoThumbnailOverrides] = useState<Record<string, string>>({});
  const [showMissingRequiredHighlights, setShowMissingRequiredHighlights] = useState(false);
  const autosaveRef = useRef<number | null>(null);

  const draftStorageKey = useMemo(
    () => `projektdokumentation-draft:${projectId}:${phaseId}`,
    [phaseId, projectId]
  );

  const activeDeal = useMemo(
    () => deals.find((deal) => deal.id === projectId) || null,
    [deals, projectId]
  );
  const driveDealName = useMemo(() => {
    const pathLabel = template?.uploadTargetInfo?.projectFolder?.pathLabel;
    const fromPath = pathLabel?.split("/")?.[0]?.trim();
    return activeDeal?.dealname || fromPath || projectId;
  }, [activeDeal?.dealname, projectId, template?.uploadTargetInfo?.projectFolder?.pathLabel]);
  const gdriveFolderId = activeDeal?.gdrive_folder_id || undefined;
  const projectFolderTarget = useMemo<ProjektdokumentationUploadTarget | null>(() => {
    const templateTarget = template?.uploadTargetInfo?.projectFolder;

    if (templateTarget?.url || !gdriveFolderId) {
      return templateTarget || null;
    }

    // Die Projektliste liefert die Drive-Ordner-ID auch dann, wenn One360 das
    // optionale uploadTargetInfo im VOT-Template nicht mitsendet.
    return {
      label: templateTarget?.label || "Google-Drive-Projektordner",
      url: `https://drive.google.com/drive/folders/${encodeURIComponent(gdriveFolderId)}`,
      pathLabel: templateTarget?.pathLabel || "Google Drive / Projektordner",
    };
  }, [gdriveFolderId, template?.uploadTargetInfo?.projectFolder]);
  const payload = useMemo(() => buildPayload(projectId, draft, user), [draft, projectId, user]);
  const validation = useMemo(() => validateProjektdokumentation(payload), [payload]);
  const currentStatus = storedRecord?.status || draft.status;
  const isApproved = currentStatus === "approved";
  const isReadOnly = disabled || isApproved;
  // Das Flag wird vom API anhand der serverseitigen Admin-Rolle geliefert.
  // Dadurch ist die Sichtbarkeit nur Komfort; der Reset-Endpunkt prüft die Rolle erneut.
  const canResetVotToDraft = isApproved && template?.canResetVotToDraft === true;
  // Ticket #1055: identischer Backend-Check wie canResetVotToDraft ("Admin" = darf einen
  // abgeschlossenen VOT wieder zum Bearbeiten öffnen, per Nutzer-Rückmeldung) — bewusst OHNE das
  // isApproved-Gate von canResetVotToDraft oben, da Fotos auch vor dem Abschluss markiert werden
  // dürfen sollen.
  const canManageVotPhotos = template?.canResetVotToDraft === true;
  const hasPendingVotPhotoUploads = Object.keys(votPhotoBusy).length > 0;

  const {
    editingPair: editingVotPhotoPair,
    editorImageDataUrl: votAnnotationImageDataUrl,
    editorOriginalImageDataUrl: votAnnotationOriginalImageDataUrl,
    editorLoading: votAnnotationLoading,
    editorError: votAnnotationError,
    savingAnnotation: savingVotAnnotation,
    openAnnotation: openVotPhotoAnnotation,
    cancelAnnotation: cancelVotPhotoAnnotation,
    saveAnnotation: saveVotPhotoAnnotation,
    deleteMarkedAnnotation: deleteMarkedVotPhotoAnnotation,
  } = usePhotoAnnotationGallery<VotDriveFile>({
    projectId,
    getUploadTarget: () => [...VOT_PHOTO_DIRECTORIES],
    onSaved: (result, caption, pair, annotatedDataUrl) => {
      const { original } = pair;
      const markedFileName = withMarkedFileNameSuffix(original.name);
      setVotDriveFiles((previous) => {
        const existingIndex = previous.findIndex(
          (file) => file.id === result.fileId || file.name === markedFileName
        );
        const updatedFile: VotDriveFile =
          existingIndex >= 0
            ? {
                ...previous[existingIndex],
                id: result.fileId,
                name: result.fileName ?? markedFileName,
                thumbnailLink: result.thumbnailLink ?? previous[existingIndex].thumbnailLink,
                webViewLink: result.webViewLink ?? previous[existingIndex].webViewLink,
                description: result.description ?? caption,
              }
            : {
                id: result.fileId,
                name: result.fileName ?? markedFileName,
                mimeType: original.mimeType,
                webViewLink: result.webViewLink ?? "",
                thumbnailLink: result.thumbnailLink,
                description: result.description ?? caption,
              };
        return sortVotDriveFiles(
          existingIndex >= 0
            ? previous.map((file, index) => (index === existingIndex ? updatedFile : file))
            : [...previous, updatedFile]
        );
      });
      setVotPhotoThumbnailOverrides((previous) => ({ ...previous, [result.fileId]: annotatedDataUrl }));
    },
    onDeleted: (markedId) => {
      setVotDriveFiles((previous) => previous.filter((file) => file.id !== markedId));
      setVotPhotoThumbnailOverrides((previous) => {
        const next = { ...previous };
        delete next[markedId];
        return next;
      });
    },
    onError: (message) => showToast("error", message, 4000),
    onSuccess: (message) => showToast("success", message, 3000),
  });
  const statusAction =
    currentStatus !== "approved"
      ? {
          label: "Abschließen",
          nextStatus: "approved" as const,
          className: "bg-green-600 text-white",
          disabled: hasPendingVotPhotoUploads || validation.missingRequiredItems.length > 0,
        }
      : null;
  const missingRequiredItems = useMemo(() => {
    const sourceItems = validation.missingRequiredItems;
    const uniqueItems = Array.from(
      new Set(
        sourceItems
          .map((item) => item.trim())
          .filter((item) => item.length > 0)
      )
    );

    return uniqueItems;
  }, [validation.missingRequiredItems]);
  const missingRequiredItemIds = useMemo(
    () => new Set(validation.missingRequiredItemIds),
    [validation.missingRequiredItemIds]
  );
  const isDirectChoiceItem = (item: ProjektdokumentationChecklistItem) =>
    item.inputType === "boolean" || item.inputType === "select";
  const isVisibleInCurrentPayload = (sectionId: string, itemId: string) =>
    isChecklistItemVisible(payload, sectionId, itemId);
  const getVotPhotoKey = (section: ProjektdokumentationSection) => `section:${section.id}`;
  const getVotPhotosForSection = (section: ProjektdokumentationSection) => {
    const prefix = getVotSectionPhotoPrefix(section);
    return sortVotDriveFiles(
      votDriveFiles.filter((file) => isVotPhotoFileNameForPrefix(prefix, file.name))
    );
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((previous) => ({
      ...previous,
      [sectionId]: !previous[sectionId],
    }));
  };

  const getBulkCompletedItem = (item: ProjektdokumentationChecklistItem) => {
    if (item.id === "low-ceiling-height-present") {
      // Bulk-Complete darf hier nicht auf "true" defaulten (fälschliche Behauptung,
      // es gäbe eine niedrige Deckenhöhe) — aber das Item muss trotzdem als
      // "checked" gelten, sonst kann die Sektion nie als vollständig markiert werden.
      // Default ist daher "false" (kein Vorkommen), vom Monteur weiterhin änderbar.
      return {
        ...item,
        checked: true,
        valueBoolean: typeof item.valueBoolean === "boolean" ? item.valueBoolean : false,
      };
    }

    if (item.inputType === "boolean") {
      return {
        ...item,
        checked: true,
        valueBoolean: typeof item.valueBoolean === "boolean" ? item.valueBoolean : true,
      };
    }

    if (item.inputType === "select") {
      return {
        ...item,
        checked: true,
        valueString: item.valueString || item.options?.[0]?.value || "",
      };
    }

    return {
      ...item,
      checked: true,
    };
  };

  const getBulkClearedItem = (item: ProjektdokumentationChecklistItem) => {
    if (item.inputType === "boolean") {
      return {
        ...item,
        checked: false,
        valueBoolean: null,
      };
    }

    if (item.inputType === "select") {
      return {
        ...item,
        checked: false,
        valueString: "",
      };
    }

    return {
      ...item,
      checked: false,
    };
  };

  const setSectionCompleted = (sectionId: string, checked: boolean) => {
    if (isReadOnly) {
      return;
    }

    setDraft((previous) => {
      let nextTechnicalQuestionsOpen = previous.technicalQuestionsOpen;
      let nextDrainAvailable = previous.drainAvailable;
      let nextPvStatus = previous.pvStatus;
      const previousPayload = buildPayload(projectId, previous, user);

      const sections = previous.sections.map((section) => {
        if (section.id !== sectionId) {
          return section;
        }

        const visibleItemIds = new Set(
          section.items
            .filter((item) => isChecklistItemVisible(previousPayload, section.id, item.id))
            .map((item) => item.id)
        );

        return {
          ...section,
          items: section.items.map((item) => {
            if (!visibleItemIds.has(item.id)) {
              return item;
            }

            const nextItem = checked ? getBulkCompletedItem(item) : getBulkClearedItem(item);

            if (item.id === "technical-questions-status") {
              nextTechnicalQuestionsOpen =
                typeof nextItem.valueBoolean === "boolean"
                  ? !nextItem.valueBoolean
                  : previous.technicalQuestionsOpen;
            }

            if (item.id === "drain-availability-recorded") {
              nextDrainAvailable =
                typeof nextItem.valueBoolean === "boolean"
                  ? nextItem.valueBoolean
                    ? "yes"
                    : "no"
                  : "";
            }

            if (item.id === "pv-status-recorded") {
              nextPvStatus =
                nextItem.valueString === "no" ||
                nextItem.valueString === "existing" ||
                nextItem.valueString === "planned"
                  ? nextItem.valueString
                  : "";
            }

            return nextItem;
          }),
        };
      });

      return {
        ...previous,
        sections,
        technicalQuestionsOpen: nextTechnicalQuestionsOpen,
        drainAvailable: nextDrainAvailable,
        pvStatus: nextPvStatus,
      };
    });
  };

  const openResolvedFolder = async (target: ProjektdokumentationUploadTarget) => {
    if (target.url) {
      window.open(target.url, "_blank", "noopener,noreferrer");
    }
  };

  const renderUploadTarget = (target: ProjektdokumentationUploadTarget) => (
    <div key={target.label} className="rounded-xl border border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40 p-4">
      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {target.label}
      </p>
      {target.url ? (
        <button
          type="button"
          onClick={() => void openResolvedFolder(target)}
          className="mt-2 inline-flex items-center text-sm font-medium text-primary-700 hover:text-primary-800 dark:text-primary-300"
        >
          Ordner öffnen
        </button>
      ) : (
        <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
          Uploads laufen über die bestehende Projekt-/Drive-Struktur
        </p>
      )}
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 break-words">
        {target.pathLabel}
      </p>
    </div>
  );

  const syncDealProjectStatus = (
    projectStatus: Partial<ProjectStatusSnapshot> | undefined,
    record?: ProjektdokumentationStoredRecord | null
  ) => {
    const existingDeal = useDealsStore.getState().deals.find((deal) => deal.id === projectId);
    if (!existingDeal) {
      return;
    }

    const existingSnapshot = existingDeal.project_status || null;
    // API liefert bei einem Status-Update einen vollständigen Snapshot zurück (kein
    // partielles Patch) — die Merge-Reihenfolge erhält vorhandene Felder als Fallback.
    const nextSnapshot = projectStatus
      ? ({
          ...(existingSnapshot || {}),
          ...projectStatus,
        } as ProjectStatusSnapshot)
      : existingSnapshot
        ? { ...existingSnapshot }
        : null;

    if (nextSnapshot && record?.status) {
      nextSnapshot.projektdokumentationApprovalStatus = record.status;
    }

    upsertDeal({
      ...existingDeal,
      project_status: nextSnapshot,
    });
  };

  useEffect(() => {
    let mounted = true;

    const applyTemplateResponse = (response: ProjektdokumentationTemplate) => {
      if (!mounted) {
        return;
      }

      setTemplate(response);
      setStoredRecord(response.existingRecord);
      setExpandedSections(
        Object.fromEntries(response.sections.map((section) => [section.id, true]))
      );

      const fromServer = toDraftState(response);
      const rawDraft = window.localStorage.getItem(draftStorageKey);
      if (rawDraft && !response.existingRecord?.approvedAt) {
        try {
          const parsed = JSON.parse(rawDraft) as LocalDraftState;
          setDraft(mergeLocalDraftWithServerState(fromServer, parsed));
        } catch {
          setDraft(fromServer);
        }
      } else {
        setDraft(fromServer);
      }

      if (response.existingRecord?.status === "approved") {
        const approvedAt = response.existingRecord.approvedAt || response.existingRecord.updatedAt;
        updatePhaseStatus(
          phaseId,
          "completed",
          approvedAt || undefined,
          response.existingRecord.completedByEmail || undefined
        );
        onCompleted?.(approvedAt || response.existingRecord.updatedAt);
      } else {
        updatePhaseStatus(phaseId, "pending");
      }

      setHydrated(true);
    };

    const loadTemplate = async () => {
      // Ticket #1055: Nutzer-Rückmeldung "es funktioniert offline nicht" — analog zu
      // ProjektvorbereitungBriefing.tsx, s. store/templateCacheStore.ts. Der Formular-Entwurf
      // selbst wurde schon vorher separat offline-fähig über draftStorageKey gehalten; hier fehlte
      // nur die Persistenz der Template-DEFINITION (Sections/Checklisten-Struktur) selbst.
      const cached = useTemplateCacheStore.getState().projektdokumentation[projectId];
      try {
        setLoading(true);
        setError(null);
        if (typeof navigator !== "undefined" && !navigator.onLine && cached) {
          applyTemplateResponse(cached);
          return;
        }
        const response = await getProjektdokumentationTemplate(projectId);
        applyTemplateResponse(response);
        useTemplateCacheStore.getState().setProjektdokumentationTemplate(projectId, response);
      } catch (loadError) {
        if (cached) {
          applyTemplateResponse(cached);
        } else {
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Projektdokumentation konnte nicht geladen werden"
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadTemplate();
    return () => {
      mounted = false;
    };
  }, [draftStorageKey, phaseId, projectId, updatePhaseStatus]);

  useEffect(() => {
    if (!template || !hydrated) {
      return;
    }

    let mounted = true;
    const loadVotFiles = async () => {
      try {
        setIsLoadingVotDriveFiles(true);
        const response = await listVotDriveFiles(projectId);
        if (mounted) {
          setVotDriveFiles(sortVotDriveFiles(response.files || []));
          setVotPhotoErrors({});
        }
      } catch (loadError) {
        if (mounted) {
          setVotPhotoErrors({
            __global:
              loadError instanceof Error
                ? loadError.message
                : "VOT-Bilder konnten nicht geladen werden",
          });
        }
      } finally {
        if (mounted) {
          setIsLoadingVotDriveFiles(false);
        }
      }
    };

    void loadVotFiles();
    return () => {
      mounted = false;
    };
  }, [hydrated, projectId, template]);

  useEffect(() => {
    if (!hydrated) {
      return;
    }

    if (isApproved) {
      window.localStorage.removeItem(draftStorageKey);
      return;
    }

    window.localStorage.setItem(draftStorageKey, JSON.stringify(draft));
  }, [draft, draftStorageKey, hydrated, isApproved]);

  useEffect(() => {
    if (!template || !hydrated || isReadOnly) {
      return;
    }

    if (autosaveRef.current) {
      window.clearTimeout(autosaveRef.current);
    }

    autosaveRef.current = window.setTimeout(() => {
      const save = async () => {
        try {
          if (navigator.onLine) {
            const response = await upsertProjektdokumentation(projectId, payload);
            setStoredRecord((previous) => toStoredRecord(response, payload, previous));
            if (response.status !== draft.status) {
              setDraft((previous) => ({
                ...previous,
                status: response.status,
              }));
            }
          } else {
            const result = await addToQueue("projektdokumentation_upsert", {
              queueKey: `projektdokumentation_upsert:${projectId}`,
              projectId,
              payload,
            });
            // Code-Review-Finding: ein sofortiger endgültiger Fehlschlag lässt addToQueue
            // normal auflösen (nur mit permanentlyFailed statt successful) - ohne diese
            // Prüfung bliebe der Autosave-Zyklus unbemerkt verloren.
            if (result && result.permanentlyFailed.length > 0) {
              throw new Error('Änderungen konnten nicht gespeichert werden.');
            }
          }
        } catch (error) {
          const status = (error as any)?.status;
          if (typeof status === 'number' && status >= 400 && status < 500) {
            return; // 4xx = permanent error, nicht in Queue schreiben
          }
          try {
            const result = await addToQueue("projektdokumentation_upsert", {
              queueKey: `projektdokumentation_upsert:${projectId}`,
              projectId,
              payload,
            });
            if (result && result.permanentlyFailed.length > 0) {
              throw new Error('Änderungen konnten nicht gespeichert werden.');
            }
          } catch (queueError) {
            if (queueError instanceof QueueItemLockTimeoutError) {
              // Ein Lock-Timeout ist ein UNBESTIMMTER Ausgang, kein definitiver Fehlschlag
              // (siehe QueueItemLockTimeoutError-Doku in queueService.ts) - addQueueItem läuft
              // im Hintergrund weiter und kann den Autosave noch erfolgreich vormerken. Kein
              // Fehler-Toast, der fälschlich einen definitiven Fehlschlag suggerieren würde
              // (PR-Review-Finding #976/#422).
              console.warn('[Projektdokumentation] Autosave-Vormerkung hängt noch (Lock-Timeout):', queueError);
              return;
            }
            // Auch die Queue-Ablage kann jetzt fehlschlagen (z.B. Speicherplatz voll,
            // siehe #1199) - ohne diesen catch würde ein Autosave-Zyklus dann komplett
            // verloren gehen, ohne dass der Monteur je etwas davon erfährt.
            console.error('[Projektdokumentation] Autosave konnte nicht in die Queue geschrieben werden:', queueError);
            showToast(
              'error',
              queueError instanceof Error ? queueError.message : 'Änderungen konnten nicht gespeichert werden.',
              5000,
            );
          }
        }
      };

      void save();
    }, 750);

    return () => {
      if (autosaveRef.current) {
        window.clearTimeout(autosaveRef.current);
      }
    };
  }, [addToQueue, hydrated, isReadOnly, payload, projectId, template]);

  const setItem = (
    sectionId: string,
    itemId: string,
    updater: (item: ProjektdokumentationChecklistItem) => ProjektdokumentationChecklistItem
  ) => {
    if (isReadOnly) {
      return;
    }

    setDraft((previous) => {
      let nextItemState: ProjektdokumentationChecklistItem | null = null;
      const sections = previous.sections.map((section) => {
        if (section.id !== sectionId) {
          return section;
        }

        const nextSection = {
          ...section,
          items: section.items.map((item) => {
            if (item.id !== itemId) {
              return item;
            }

            nextItemState = updater(item);
            return nextItemState;
          }),
        };

        if (itemId === "drain-availability-recorded" && nextItemState?.valueBoolean === false) {
          nextSection.items = nextSection.items.map((item) =>
            item.id === "drain-documented"
              ? { ...item, checked: false, comment: undefined }
              : item
          );
        }

        if (itemId === "special-challenges-documented" && nextItemState?.checked === false) {
          nextSection.items = nextSection.items.map((item) =>
            item.id === "special-challenges-structural" ||
            item.id === "special-challenges-technical" ||
            item.id === "special-challenges-organizational"
              ? { ...item, checked: false, comment: undefined }
              : item
          );
        }

        return nextSection;
      });

      return {
        ...previous,
        sections,
        ...updateBoundFieldsForItem(previous, itemId, nextItemState || ({} as ProjektdokumentationChecklistItem)),
      };
    });
  };

  const mergeVotDriveFilesForPrefix = (prefix: string, files: VotDriveFile[]) => {
    setVotDriveFiles((previous) =>
      sortVotDriveFiles([
        ...previous.filter((file) => !isVotPhotoFileNameForPrefix(prefix, file.name)),
        ...files,
      ])
    );
  };

  const handleVotPhotoFiles = async (
    section: ProjektdokumentationSection,
    selectedFiles: File[]
  ) => {
    if (!isVotPhotoSectionSupported(section.id, section.code)) {
      return;
    }

    if (isReadOnly || selectedFiles.length === 0) {
      return;
    }

    const imageFiles = selectedFiles.filter((file) => file.type.startsWith("image/"));
    if (imageFiles.length === 0) {
      showToast("error", "Bitte nur Bilddateien auswählen", 3000);
      return;
    }

    const key = getVotPhotoKey(section);
    const prefix = getVotSectionPhotoPrefix(section);
    setVotPhotoBusy((previous) => ({ ...previous, [key]: "uploading" }));
    setVotPhotoErrors((previous) => {
      const next = { ...previous };
      delete next[key];
      return next;
    });

    try {
      const latest = await listVotDriveFiles(projectId, prefix);
      const latestSectionFiles = latest.files.filter((file) =>
        isVotPhotoFileNameForPrefix(prefix, file.name)
      );
      const fileNames = getNextVotPhotoFileNames(
        prefix,
        latestSectionFiles.map((file) => file.name),
        imageFiles.length
      );
      const normalizedFiles = await Promise.all(
        imageFiles.map((file, index) => normalizeImageForVotUpload(file, fileNames[index]))
      );
      const uploadResult = await uploadFilesToDrive(
        normalizedFiles,
        driveDealName,
        [...VOT_PHOTO_DIRECTORIES],
        { sharedDrive: getSharedDriveName(), gdriveFolderId },
      );

      if (!uploadResult.status || !uploadResult.files) {
        throw new Error(uploadResult.message || "VOT-Bilder konnten nicht hochgeladen werden");
      }

      const uploadedFiles: VotDriveFile[] = uploadResult.files.map((file, index) => ({
        id: file.fileId,
        name: file.fileName || normalizedFiles[index].name,
        mimeType: "image/jpeg",
        webViewLink: file.webViewLink,
      }));

      mergeVotDriveFilesForPrefix(prefix, [
        ...latestSectionFiles,
        ...uploadedFiles,
      ]);
      showToast("success", `${uploadedFiles.length} VOT-Bild(er) hochgeladen`, 2500);
    } catch (uploadError) {
      const message =
        uploadError instanceof Error ? uploadError.message : "VOT-Bilder konnten nicht hochgeladen werden";
      setVotPhotoErrors((previous) => ({ ...previous, [key]: message }));
      showToast("error", message, 4000);
    } finally {
      setVotPhotoBusy((previous) => {
        const next = { ...previous };
        delete next[key];
        return next;
      });
    }
  };

  const selectVotPhotosFromAlbum = (section: ProjektdokumentationSection) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;
    input.style.display = "none";
    input.onchange = () => {
      void handleVotPhotoFiles(section, Array.from(input.files || []));
      input.remove();
    };
    document.body.appendChild(input);
    input.click();
  };

  const captureVotPhoto = (section: ProjektdokumentationSection) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.setAttribute("capture", "environment");
    input.style.display = "none";
    input.onchange = () => {
      void handleVotPhotoFiles(section, Array.from(input.files || []));
      input.remove();
    };
    document.body.appendChild(input);
    input.click();
  };

  const renderVotPhotoControls = (section: ProjektdokumentationSection) => {
    if (!isVotPhotoSectionSupported(section.id, section.code)) {
      return null;
    }

    const key = getVotPhotoKey(section);
    const photos = getVotPhotosForSection(section);
    const isBusy = !!votPhotoBusy[key];
    const errorMessage = votPhotoErrors[key];

    return (
      <div className="mx-4 mb-4 mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              VOT-Bilder
            </p>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
              {photos.length > 0
                ? `${photos.length} Bild(er) in Bilder/VOT`
                : isLoadingVotDriveFiles
                  ? "Bilder werden geladen..."
                  : "Noch keine Bilder hinterlegt"}
            </p>
          </div>
          {!isReadOnly && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={isBusy}
                onClick={() => captureVotPhoto(section)}
                className="rounded-md bg-primary-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
              >
                Foto aufnehmen
              </button>
              <button
                type="button"
                disabled={isBusy}
                onClick={() => selectVotPhotosFromAlbum(section)}
                className="rounded-md border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200"
              >
                Aus Album wählen
              </button>
            </div>
          )}
        </div>

        {isBusy && (
          <p className="mt-2 text-xs text-primary-700 dark:text-primary-300">
            Bilder werden hochgeladen...
          </p>
        )}
        {errorMessage && (
          <p className="mt-2 text-xs text-red-700 dark:text-red-300">
            {errorMessage}
          </p>
        )}
        {photos.length > 0 && (
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {pairVotPhotos(photos).map((pair) => {
              const displayPhoto = pair.marked ?? pair.original;
              const thumbnailSrc = votPhotoThumbnailOverrides[displayPhoto.id] ?? displayPhoto.thumbnailLink;
              const tileContent = (
                <>
                  <div className="relative mb-2 h-20 w-full">
                    {thumbnailSrc ? (
                      <img
                        src={thumbnailSrc}
                        alt={displayPhoto.name}
                        className="h-20 w-full rounded object-cover"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="flex h-20 items-center justify-center rounded bg-gray-100 text-gray-400 dark:bg-gray-800">
                        Bild
                      </div>
                    )}
                    {displayPhoto.description && (
                      <CaptionIndicator caption={displayPhoto.description} />
                    )}
                  </div>
                  <span className="block truncate" title={displayPhoto.name}>
                    {displayPhoto.name}
                  </span>
                </>
              );

              // Ticket #1055: nur Ops-Manager (identischer Backend-Check wie canResetVotToDraft,
              // per Nutzer-Rückmeldung) dürfen VOT-eigene Fotos markieren — für alle anderen bleibt
              // das bisherige Verhalten (Klick öffnet webViewLink in neuem Tab) unverändert.
              if (canManageVotPhotos) {
                return (
                  <button
                    key={`${pair.original.id}-${pair.original.name}`}
                    type="button"
                    onClick={() => openVotPhotoAnnotation(pair)}
                    className="min-w-0 rounded-md border border-gray-200 bg-white p-2 text-left text-xs text-gray-700 hover:border-primary-400 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200"
                  >
                    {tileContent}
                  </button>
                );
              }

              return (
                <a
                  key={`${pair.original.id}-${pair.original.name}`}
                  href={displayPhoto.webViewLink}
                  target="_blank"
                  rel="noreferrer"
                  className="min-w-0 rounded-md border border-gray-200 bg-white p-2 text-xs text-gray-700 hover:border-primary-400 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200"
                >
                  {tileContent}
                </a>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Ticket #1055: Markierungs-Editor-Modal für VOT-eigene Fotos — bewusst eine leichte Kopie der
  // JSX-Hülle aus ProjektvorbereitungBriefing.tsx statt einer weiteren gemeinsamen Abstraktion
  // (nur der Zustandsautomat ist gemeinsam, s. usePhotoAnnotationGallery).
  const renderVotPhotoAnnotationModal = () => {
    if (!editingVotPhotoPair) {
      return null;
    }

    const displayName = (editingVotPhotoPair.marked ?? editingVotPhotoPair.original).name;

    return (
      <div className="fixed inset-0 z-50 flex flex-col sm:items-center sm:justify-center sm:bg-black sm:bg-opacity-75 sm:p-4 sm:pb-20">
        <div className="flex flex-col w-full h-full bg-white dark:bg-gray-800 sm:w-full sm:h-auto sm:max-w-2xl sm:max-h-[calc(90vh-60px)] sm:rounded-lg sm:shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
              Foto markieren: {displayName}
            </h2>
            <button
              onClick={cancelVotPhotoAnnotation}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors shrink-0 ml-2"
              aria-label="Schließen"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-auto p-4 bg-gray-50 dark:bg-gray-900">
            {votAnnotationLoading ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Foto wird geladen</p>
              </div>
            ) : votAnnotationError || !votAnnotationImageDataUrl ? (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
                {votAnnotationError || "Foto konnte nicht geladen werden"}
              </div>
            ) : (
              <PhotoAnnotationEditor
                imageDataUrl={votAnnotationImageDataUrl}
                originalImageDataUrl={votAnnotationOriginalImageDataUrl}
                initialCaption={(editingVotPhotoPair.marked ?? editingVotPhotoPair.original).description}
                onSave={saveVotPhotoAnnotation}
                onDelete={editingVotPhotoPair.marked ? deleteMarkedVotPhotoAnnotation : undefined}
                onCancel={cancelVotPhotoAnnotation}
                saving={savingVotAnnotation}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const applyLocalStatus = (
    status: ProjektfreigabeStatus,
    record?: ProjektdokumentationStoredRecord | null
  ) => {
    if (record) {
      setStoredRecord(record);
      setDraft((previous) => ({
        ...previous,
        status: record.status,
        sections: record.sections,
      }));
      return;
    }

    setDraft((previous) => ({ ...previous, status }));
  };

  const setStatus = async (status: ProjektfreigabeStatus) => {
    if (isReadOnly || isSavingStatus) {
      return;
    }

    // Der Abschluss erzeugt ein PDF und speichert dessen Drive-Link in HubSpot.
    // Das lässt sich offline nicht vollständig und verlässlich in die Queue legen.
    if (status === "approved" && !navigator.onLine) {
      showToast("error", "Für den Abschluss und den VOT-Bericht ist eine Internetverbindung erforderlich", 4000);
      return;
    }

    if (hasPendingVotPhotoUploads) {
      showToast("error", "Bitte warte, bis alle VOT-Bilder hochgeladen sind", 3000);
      return;
    }

    setIsSavingStatus(true);

    const optimisticApprovedAt = new Date().toISOString();

    try {
      if (navigator.onLine) {
        const submittedAt = new Date().toISOString();
        let votReportLink: string | null = null;

        if (status === "approved") {
          const savedRecord = await upsertProjektdokumentation(projectId, payload);
          setStoredRecord((previous) => toStoredRecord(savedRecord, payload, previous));
          const report = await submitVotReport(projectId, {
            projectId,
            submittedAt,
            submittedByEmail: user?.email,
            entries: buildVotReportEntries(
              payload,
              (section) => getVotPhotosForSection(section).length > 0
            ),
          });
          const pdfFile = base64ToFile(
            report.file.base64Content,
            report.file.fileName,
            report.file.mimeType
          );
          const uploadResult = await uploadFilesToDrive(
            [pdfFile],
            report.dealName || projectId,
            report.targetDirectories,
            { sharedDrive: getSharedDriveName(), gdriveFolderId },
          );
          const uploadedFile = uploadResult.files?.[0];
          if (!uploadedFile?.webViewLink) {
            throw new Error("VOT-PDF wurde erzeugt, aber nicht in Google Drive gespeichert");
          }
          votReportLink = uploadedFile.webViewLink;
        }

        const record = await updateProjektdokumentationStatus(projectId, {
          projectId,
          status,
          votReportLink: votReportLink ?? undefined,
        });
        applyLocalStatus(status, record);
        syncDealProjectStatus(undefined, record);

        if (status === "approved") {
          const approvedAt = record.approvedAt || optimisticApprovedAt;
          updatePhaseStatus(
            phaseId,
            "completed",
            approvedAt,
            record.completedByEmail || undefined
          );
          onCompleted?.(approvedAt);
          showToast("success", "Projektdokumentation freigegeben", 3000);
        } else {
          showToast("success", `Status aktualisiert: ${statusLabels[status]}`, 2500);
        }
      } else {
        applyLocalStatus(status);
        await addToQueue("projektdokumentation_status_update", {
          queueKey: `projektdokumentation_status_update:${projectId}`,
          projectId,
          phaseId,
          payload: {
            projectId,
            status,
          },
        });

        if (status === "approved") {
          updatePhaseStatus(phaseId, "completed", optimisticApprovedAt);
          onCompleted?.(optimisticApprovedAt);
        }
        showToast("info", "Status wurde offline in die Queue gelegt", 3000);
      }
    } catch (statusError) {
      if (statusError instanceof QueueItemLockTimeoutError) {
        // Ein Lock-Timeout ist ein UNBESTIMMTER Ausgang, kein definitiver Fehlschlag (siehe
        // QueueItemLockTimeoutError-Doku in queueService.ts) - der lokale Status wurde oben
        // bereits optimistisch gesetzt und addQueueItem kann im Hintergrund noch erfolgreich
        // abschließen. Kein Fehler-Toast, der fälschlich einen definitiven Fehlschlag
        // suggerieren würde (PR-Review-Finding #976/#422).
        console.warn('[Projektdokumentation] Status-Vormerkung hängt noch (Lock-Timeout):', statusError);
        showToast("info", "Status wird noch synchronisiert", 3000);
      } else {
        showToast(
          "error",
          statusError instanceof Error ? statusError.message : "Status konnte nicht aktualisiert werden",
          4000
        );
      }
    } finally {
      setIsSavingStatus(false);
    }
  };

  const requestStatusChange = (status: ProjektfreigabeStatus) => {
    if (status === "approved" && missingRequiredItems.length > 0) {
      setShowMissingRequiredHighlights(true);
      expandMissingRequiredSections();
      return;
    }

    void setStatus(status);
  };

  const requestResetToDraft = () => {
    if (!canResetVotToDraft || isResettingToDraft) {
      return;
    }

    if (!navigator.onLine) {
      showToast("error", "Für das Zurücksetzen des VOT ist eine Internetverbindung erforderlich", 4000);
      return;
    }

    const confirmed = window.confirm(
      "VOT zurück in Bearbeitung setzen? Der VOT wird auf Entwurf gesetzt und die HubSpot-Felder „VOT abgeschlossen am“ sowie „VOT-Status“ werden geleert."
    );
    if (!confirmed) {
      return;
    }

    void (async () => {
      setIsResettingToDraft(true);
      try {
        const record = await resetVotToDraft(projectId);
        applyLocalStatus("draft", record);
        syncDealProjectStatus(undefined, record);
        updatePhaseStatus(phaseId, "pending");

        showToast("success", "VOT ist wieder in Bearbeitung", 3000);
      } catch (resetError) {
        showToast(
          "error",
          resetError instanceof Error
            ? resetError.message
            : "VOT konnte nicht zurückgesetzt werden",
          4000
        );
      } finally {
        setIsResettingToDraft(false);
      }
    })();
  };

  const expandMissingRequiredSections = () => {
    if (missingRequiredItemIds.size === 0) {
      return;
    }

    setExpandedSections((previous) => ({
      ...previous,
      ...Object.fromEntries(
        payload.sections
          .filter((section) =>
            section.items.some((item) => missingRequiredItemIds.has(item.id))
          )
          .map((section) => [section.id, true])
      ),
    }));
  };

  const renderChecklistItemHelp = (item: ProjektdokumentationChecklistItem) => {
    if (!item.helpText) {
      return null;
    }

    return (
      <details className="mt-1">
        <summary
          className="inline-flex h-5 w-5 cursor-pointer list-none items-center justify-center rounded-full border border-gray-300 text-xs font-medium text-gray-500 marker:content-none [&::-webkit-details-marker]:hidden dark:border-gray-600 dark:text-gray-400"
          aria-label={`Hilfe zu "${item.label}" anzeigen`}
        >
          ?
        </summary>
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{item.helpText}</p>
      </details>
    );
  };

  const renderItemInput = (
    sectionId: string,
    item: ProjektdokumentationChecklistItem,
    isHighlighted = false
  ) => {
    if (!item.inputType || item.inputType === "none") {
      return null;
    }

    const commonClassName =
      `mt-3 w-full rounded-lg border bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-white ${
        isHighlighted
          ? "border-red-500 ring-1 ring-red-500 focus:border-red-500 focus:ring-red-500"
          : "border-gray-300 dark:border-gray-700"
      }`;

    if (item.inputType === "text" || item.inputType === "url") {
      const value = item.inputType === "url" ? item.valueUrl || "" : item.valueString || "";
      return (
        <input
          type={item.inputType === "url" ? "url" : "text"}
          value={value}
          disabled={isReadOnly}
          onChange={(event) =>
            setItem(sectionId, item.id, (current) => ({
              ...current,
              [item.inputType === "url" ? "valueUrl" : "valueString"]: event.target.value,
              checked: current.checked || event.target.value.trim().length > 0,
            }))
          }
          className={commonClassName}
          placeholder={item.inputPlaceholder || item.inputLabel || "Eintragen"}
        />
      );
    }

    if (item.inputType === "number") {
      return (
        <input
          type="text"
          inputMode="numeric"
          value={typeof item.valueNumber === "number" ? String(item.valueNumber) : ""}
          disabled={isReadOnly}
          onChange={(event) => {
            const raw = event.target.value;
            if (raw !== "" && !/^\d+$/.test(raw)) {
              return;
            }

            setItem(sectionId, item.id, (current) => ({
              ...current,
              valueNumber: raw === "" ? null : Number(raw),
              checked: current.checked || raw !== "",
            }));
          }}
          className={commonClassName}
          placeholder={item.inputPlaceholder || "Zahl eingeben"}
        />
      );
    }

    if (item.inputType === "boolean") {
      const selectedValue =
        typeof item.valueBoolean === "boolean" ? (item.valueBoolean ? "yes" : "no") : "";
      const booleanOptions =
        item.id === "technical-questions-status"
          ? [
              { value: true, label: "Ja (keine offen)" },
              { value: false, label: "Nein (es gibt offene)" },
            ]
          : [
              { value: true, label: "Ja" },
              { value: false, label: "Nein" },
            ];
      return (
        <div
          className={`mt-3 grid grid-cols-2 gap-3 rounded-lg ${
            isHighlighted ? "border border-red-500 p-1" : ""
          }`}
        >
          {booleanOptions.map((option) => {
            const isSelected =
              typeof item.valueBoolean === "boolean" && item.valueBoolean === option.value;

            return (
              <button
                key={option.label}
                type="button"
                disabled={isReadOnly}
                onClick={() =>
                  setItem(sectionId, item.id, (current) => ({
                    ...current,
                    valueBoolean: option.value,
                    checked: true,
                  }))
                }
                className={`min-h-[48px] rounded-lg border px-4 py-3 text-sm font-medium ${
                  isSelected
                    ? "border-primary-500 bg-primary-50 text-primary-800 dark:bg-primary-950/40 dark:text-primary-200"
                    : "border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300"
                }`}
                aria-pressed={selectedValue === (option.value ? "yes" : "no")}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      );
    }

    if (item.inputType === "select") {
      return (
        <select
          value={item.valueString || ""}
          disabled={isReadOnly}
          onChange={(event) =>
            setItem(sectionId, item.id, (current) => ({
              ...current,
              valueString: event.target.value,
              checked: event.target.value !== "",
            }))
          }
          className={commonClassName}
        >
          {!item.valueString && <option value="">Bitte wählen</option>}
          {(item.options || []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
        {error || "Projektdokumentation konnte nicht geladen werden"}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow overflow-hidden">
        <div className="p-5 border-b dark:border-gray-700 bg-gradient-to-br from-slate-50 to-white dark:from-gray-800 dark:to-gray-800">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="max-w-[18rem]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-300">
                Sales → OPS
              </p>
              <h2 className="mt-2 text-xl font-semibold leading-7 text-gray-900 dark:text-white">
                Freigabe-Checkliste
              </h2>
              <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                Unterlagen so vollständig erfassen, dass Planung, Bestellung und Umsetzung direkt
                starten können.
              </p>
            </div>
            <div className="flex flex-col items-start gap-2 sm:items-end">
              <span className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${statusTone[currentStatus]}`}>
                {statusLabels[currentStatus]}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {missingRequiredItems.length === 0
                  ? "Alle Pflichtpunkte erfüllt"
                  : `${missingRequiredItems.length} Pflichtpunkte offen`}
              </span>
            </div>
          </div>
        </div>

        <div className="p-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40 p-3">
            <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Fortschritt
            </p>
            <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
              {storedRecord?.completenessPercent ?? validation.completenessPercent}%
            </p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40 p-3">
            <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Sichtbar
            </p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
              {currentStatus === "approved"
                ? "Freigegeben"
                : validation.isReadyForApproval
                  ? "Abschlussbereit"
                  : "Unvollständig"}
            </p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40 p-3 col-span-2">
            <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Letzte Änderung
            </p>
            <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
              {storedRecord?.updatedAt
                ? new Date(storedRecord.updatedAt).toLocaleString("de-DE")
                : "Noch nicht gespeichert"}
            </p>
          </div>
        </div>
      </div>

      {projectFolderTarget && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Upload-Ziel
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Anzeige aus der bestehenden HubSpot-/Drive-Infrastruktur. Diese Maske pflegt die Links nicht selbst.
              </p>
            </div>
            <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 dark:bg-gray-900 dark:text-gray-300">
              {template?.uploadTargetInfo?.source === "hubspot" ? "Quelle: HubSpot" : "Quelle: bestehende Drive-Struktur"}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4">
            {renderUploadTarget(projectFolderTarget)}
          </div>
        </div>
      )}

      {votPhotoErrors.__global && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {votPhotoErrors.__global}
        </div>
      )}

      {draft.sections.map((section) => {
        const progress = getSectionProgress(payload, section);
        const isExpanded = !!expandedSections[section.id];
        const visibleItems = section.items.filter((item) =>
          isVisibleInCurrentPayload(section.id, item.id)
        );
        const isSectionChecked =
          visibleItems.length > 0 && visibleItems.every((item) => item.checked);
        return (
          <div key={section.id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection(section.id)}
              className="flex w-full items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 text-left dark:border-gray-700 dark:bg-gray-900/40"
            >
              <span className="min-w-0">
                <span className="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {section.code}
                </span>
                <span className="mt-1 block text-base font-semibold text-gray-900 dark:text-white">
                  {section.title}
                </span>
                {section.description && (
                  <span className="mt-1 block text-sm text-gray-600 dark:text-gray-300">
                    {section.description}
                  </span>
                )}
              </span>
              <span
                className={`text-gray-500 transition-transform dark:text-gray-400 ${
                  isExpanded ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              >
                ▼
              </span>
            </button>

            <div className="px-4 py-3 border-b dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={isSectionChecked}
                  disabled={isReadOnly}
                  onChange={(event) => setSectionCompleted(section.id, event.target.checked)}
                  className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                {section.bulkToggleLabel || "Alle Unterpunkte"}
              </label>
              <div className="text-right">
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                    progress.isComplete
                      ? "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-200"
                      : "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                  }`}
                >
                  {progress.isComplete ? "vollständig" : "unvollständig"}
                </span>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  {progress.completed} / {progress.total}
                </p>
              </div>
            </div>
            {renderVotPhotoControls(section)}

            {isExpanded && (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {visibleItems.map((item) => {
                  const completed = isChecklistItemCompleted(item);
                  const isMissingHighlighted =
                    showMissingRequiredHighlights && missingRequiredItemIds.has(item.id);
                  return (
                    <div
                      key={item.id}
                      className={`p-4 ${
                        isMissingHighlighted
                          ? "bg-red-50/80 outline outline-2 -outline-offset-2 outline-red-500 dark:bg-red-950/20"
                          : ""
                      }`}
                    >
                      {isDirectChoiceItem(item) ? (
                        <div>
                          <span className="block text-sm font-medium text-gray-900 dark:text-white">
                            {item.label} {item.required ? "*" : ""}
                          </span>
                          <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                            {completed ? "Erfüllt" : item.required ? "Pflichtpunkt" : "Optional"}
                          </span>
                          {renderChecklistItemHelp(item)}
                        </div>
                      ) : (
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={item.checked}
                            disabled={isReadOnly}
                            onChange={(event) =>
                              setItem(section.id, item.id, (current) => ({
                                ...current,
                                checked: event.target.checked,
                              }))
                            }
                            className={`mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                              isMissingHighlighted ? "ring-2 ring-red-500" : ""
                            }`}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-gray-900 dark:text-white">
                              {item.label} {item.required ? "*" : ""}
                            </span>
                            <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                              {completed ? "Erfüllt" : item.required ? "Pflichtpunkt" : "Optional"}
                            </span>
                            {renderChecklistItemHelp(item)}
                          </span>
                        </label>
                      )}

                      {renderItemInput(section.id, item, isMissingHighlighted)}

                      {isMissingHighlighted && (
                        <p className="mt-2 text-xs font-medium text-red-700 dark:text-red-300">
                          Pflichtfeld fehlt.
                        </p>
                      )}

                      <textarea
                        value={item.comment || ""}
                        disabled={isReadOnly}
                        onChange={(event) =>
                          setItem(section.id, item.id, (current) => ({
                            ...current,
                            comment: event.target.value,
                          }))
                        }
                        rows={2}
                        className="mt-3 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-white"
                        placeholder="Bemerkung optional"
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          Vollständigkeit & Freigabe
        </h3>

        {missingRequiredItems.length > 0 ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
            <p className="font-medium">Fehlende Pflichtpunkte</p>
            <ul className="mt-2 list-disc pl-5 text-sm space-y-1">
              {missingRequiredItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 text-green-900">
              <p className="font-medium">Alle Pflichtpunkte sind erfüllt.</p>
              </div>
            )}

            {storedRecord?.completedAt && (
              <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
                <p>
                  Abgeschlossen am {new Date(storedRecord.completedAt).toLocaleString("de-DE")}
                  {(() => {
                    const completedByName =
                      storedRecord.completedBy?.username?.trim() || (storedRecord.completedByEmail ?? "").trim();
                    return completedByName ? ` von ${completedByName}` : "";
                  })()}
                  .
                </p>
            {storedRecord.approvedAt && (
              <p className="mt-1">
                Freigegeben am {new Date(storedRecord.approvedAt).toLocaleString("de-DE")}
                {(() => {
                  const approvedByName =
                    storedRecord.approvedBy?.username?.trim() || (storedRecord.approvedByEmail ?? "").trim();
                  return approvedByName ? ` von ${approvedByName}` : "";
                })()}
                .
              </p>
            )}
          </div>
        )}

        {statusAction && (
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => requestStatusChange(statusAction.nextStatus)}
              disabled={isReadOnly || isSavingStatus || statusAction.disabled}
              className={`min-h-[48px] rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 ${statusAction.className}`}
            >
              {isSavingStatus && statusAction.nextStatus === "approved"
                ? "Wird abgeschlossen..."
                : statusAction.label}
            </button>
          </div>
        )}

        {canResetVotToDraft && (
          <div className="mt-4">
            <button
              type="button"
              onClick={requestResetToDraft}
              disabled={isResettingToDraft}
              className="min-h-[48px] rounded-lg border border-amber-500 px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-50 dark:border-amber-400 dark:text-amber-200 dark:hover:bg-amber-950/30"
            >
              {isResettingToDraft ? "Wird zurückgesetzt..." : "Zurück in Bearbeitung"}
            </button>
          </div>
        )}
      </div>
      {renderVotPhotoAnnotationModal()}
    </div>
  );
};

export default ProjektdokumentationWizard;
